import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, Eye, CreditCard, CheckCircle, UserPlus, 
  Search, ChevronLeft, ChevronRight, Activity, 
  BarChart3, Clock, User, FileText, ShieldAlert,
  Unlock, AlertTriangle, Globe
} from "lucide-react";

const ACTION_LABELS = {
  view_transaction_detail: { label: "Consultation transaction", icon: Eye, color: "bg-blue-100 text-blue-700" },
  confirm_payment: { label: "Confirmation paiement", icon: CreditCard, color: "bg-green-100 text-green-700" },
  verify_tickets: { label: "Vérification billets", icon: CheckCircle, color: "bg-emerald-100 text-emerald-700" },
  promote_to_partner: { label: "Promotion partenaire", icon: UserPlus, color: "bg-purple-100 text-purple-700" },
  transfer_tickets: { label: "Transfert billets", icon: FileText, color: "bg-amber-100 text-amber-700" },
  send_payment: { label: "Envoi paiement", icon: CreditCard, color: "bg-orange-100 text-orange-700" },
  brute_force_blocked: { label: "IP bloquée (brute force)", icon: ShieldAlert, color: "bg-red-100 text-red-700" },
  unblock_ip: { label: "IP débloquée", icon: Unlock, color: "bg-teal-100 text-teal-700" },
};

const getActionInfo = (action) => {
  return ACTION_LABELS[action] || { label: action, icon: Activity, color: "bg-gray-100 text-gray-700" };
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    action: "",
    admin_email: "",
    resource_type: "",
  });
  const [showStats, setShowStats] = useState(true);
  const [blockedIps, setBlockedIps] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchBlockedIps();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "30" });
      if (filters.action) params.append("action", filters.action);
      if (filters.admin_email) params.append("admin_email", filters.admin_email);
      if (filters.resource_type) params.append("resource_type", filters.resource_type);

      const response = await axios.get(`${API}/admin/audit-logs?${params}`);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching audit stats:", error);
    }
  };

  const fetchBlockedIps = async () => {
    try {
      const response = await axios.get(`${API}/admin/blocked-ips`);
      setBlockedIps(response.data);
    } catch (error) {
      console.error("Error fetching blocked IPs:", error);
    }
  };

  const handleUnblockIp = async (ip) => {
    try {
      await axios.delete(`${API}/admin/blocked-ips/${encodeURIComponent(ip)}`);
      fetchBlockedIps();
      fetchLogs();
    } catch (error) {
      console.error("Error unblocking IP:", error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="audit-log-viewer">
      {/* Stats Overview */}
      {showStats && stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total d'actions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_logs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Admins actifs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.admin_breakdown?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Types d'actions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.action_breakdown?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Blocked IPs Alert */}
      {blockedIps && blockedIps.active_blocks?.length > 0 && (
        <Card className="border-2 border-red-300 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="h-5 w-5 text-red-600" />
              <h3 className="font-bold text-red-800">
                {blockedIps.active_blocks.length} IP{blockedIps.active_blocks.length > 1 ? "s" : ""} bloquée{blockedIps.active_blocks.length > 1 ? "s" : ""}
              </h3>
              <Badge className="bg-red-200 text-red-800">
                {blockedIps.total_failed_attempts_24h} tentatives échouées (24h)
              </Badge>
            </div>
            <div className="space-y-2">
              {blockedIps.active_blocks.map((block, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-200"
                  data-testid={`blocked-ip-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-red-500" />
                    <div>
                      <p className="font-mono font-bold text-sm text-gray-900">{block.ip}</p>
                      <p className="text-xs text-gray-500">
                        {block.reason} | Email: {block.last_email_tried}
                      </p>
                      <p className="text-xs text-red-600">
                        Bloqué jusqu'à: {formatDate(block.blocked_until)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    onClick={() => handleUnblockIp(block.ip)}
                    data-testid={`unblock-ip-${idx}`}
                  >
                    <Unlock className="h-4 w-4 mr-1" />
                    Débloquer
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Filtrer par admin..."
                value={filters.admin_email}
                onChange={(e) => handleFilterChange("admin_email", e.target.value)}
                className="h-9"
                data-testid="audit-filter-admin"
              />
            </div>
            <Select
              value={filters.action || "all"}
              onValueChange={(v) => handleFilterChange("action", v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[200px] h-9" data-testid="audit-filter-action">
                <SelectValue placeholder="Toutes les actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                <SelectItem value="view_transaction_detail">Consultation transaction</SelectItem>
                <SelectItem value="confirm_payment">Confirmation paiement</SelectItem>
                <SelectItem value="verify_tickets">Vérification billets</SelectItem>
                <SelectItem value="promote_to_partner">Promotion partenaire</SelectItem>
                <SelectItem value="brute_force_blocked">IP bloquée (brute force)</SelectItem>
                <SelectItem value="unblock_ip">IP débloquée</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.resource_type || "all"}
              onValueChange={(v) => handleFilterChange("resource_type", v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[180px] h-9" data-testid="audit-filter-resource">
                <SelectValue placeholder="Toutes les ressources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les ressources</SelectItem>
                <SelectItem value="transaction">Transactions</SelectItem>
                <SelectItem value="user">Utilisateurs</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowStats(!showStats)}
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              {showStats ? "Masquer stats" : "Voir stats"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-brand-600" />
                Journal d'audit
              </CardTitle>
              <CardDescription>
                {total} entrée{total > 1 ? "s" : ""} - Conformité Loi 25/RGPD
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              Page {page}/{totalPages || 1}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Aucune entrée d'audit</p>
              <p className="text-sm mt-1">Les actions admin seront enregistrées ici</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, idx) => {
                const actionInfo = getActionInfo(log.action);
                const ActionIcon = actionInfo.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                    data-testid={`audit-log-entry-${idx}`}
                  >
                    <div className={`p-2 rounded-lg ${actionInfo.color}`}>
                      <ActionIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={actionInfo.color}>{actionInfo.label}</Badge>
                        {log.resource_type && (
                          <Badge variant="outline" className="text-xs">
                            {log.resource_type}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-700">
                        {log.details || `${log.action} sur ${log.resource_type || "système"}`}
                      </div>
                      {log.target_user && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <User className="h-3 w-3" />
                          <span>{log.target_user}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {formatDate(log.timestamp)}
                      </div>
                      <div className="text-xs text-brand-600 font-medium mt-0.5">
                        {log.admin_email}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </Button>
              <span className="text-sm text-gray-500">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogViewer;
